import { z } from 'zod'
import { err, ok } from '../utils/result.ts'
import type { AIModel } from '../types/enums.ts'
import type { Env } from '../types/env.ts'
import { buildBatchPrompt } from './prompt.ts'
import type { AIIntegration, BatchMentionResult, BatchResponse } from './types.ts'
import { SENTIMENTS } from '../types/enums.ts'

const MODEL_IDS: Record<'perplexity-sonar' | 'perplexity-sonar-pro', string> = {
  'perplexity-sonar':     'sonar',
  'perplexity-sonar-pro': 'sonar-pro',
}

const PerplexityResponseSchema = z.object({
  choices: z.array(z.object({
    message: z.object({ content: z.string() }),
  })).min(1),
  usage: z.object({
    prompt_tokens: z.number().nullable().optional(),
    completion_tokens: z.number().nullable().optional(),
  }).optional(),
})

const ToolResultSchema = z.object({
  results: z.array(z.object({
    prompt_id:           z.string(),
    mentioned:           z.boolean(),
    rank_position:       z.number().int().nullable().optional(),
    sentiment:           z.enum(SENTIMENTS).nullable().optional(),
    context_snippet:     z.string().nullable().optional(),
    competitor_mentions: z.array(z.string()).optional(),
    ai_answer:           z.string().optional(),
  })),
})

type PerplexityVariant = 'perplexity-sonar' | 'perplexity-sonar-pro'

export function createPerplexityIntegration(variant: PerplexityVariant): AIIntegration {
  return {
    model: variant as AIModel,

    async queryBatch(params, env: Env) {
      const prompt = buildBatchPrompt({ ...params, jsonMode: true })
      let res: Response
      try {
        res = await fetch('https://api.perplexity.ai/chat/completions', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${env.PERPLEXITY_KEY}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            model: MODEL_IDS[variant],
            messages: [{ role: 'user', content: prompt }],
            max_tokens: 4096,
            temperature: 0.1,
          }),
        })
      } catch (e) {
        return err(`Perplexity network error: ${e instanceof Error ? e.message : String(e)}`)
      }

      if (!res.ok) {
        const text = await res.text().catch(() => '')
        return err(`Perplexity API error ${res.status}: ${text}`)
      }

      let data: unknown
      try { data = await res.json() } catch {
        return err('Perplexity returned invalid JSON')
      }

      const parsed = PerplexityResponseSchema.safeParse(data)
      if (!parsed.success) return err(`Perplexity response parse error: ${parsed.error.message}`)

      const content = parsed.data.choices[0]?.message.content ?? ''
      const promptMap = new Map(params.prompts.map((p) => [p.id, p.promptText]))
      const usage = {
        inputTokens: parsed.data.usage?.prompt_tokens ?? null,
        outputTokens: parsed.data.usage?.completion_tokens ?? null,
      }

      const fallbackResults = (): BatchMentionResult[] =>
        params.prompts.map((p) => ({
          keywordId: p.id,
          promptText: p.promptText,
          mentioned: false,
          rankPosition: null,
          sentiment: null,
          contextSnippet: null,
          competitorMentions: [],
          aiAnswer: null,
        }))

      const jsonMatch = content.match(/\{[\s\S]*\}/)
      if (!jsonMatch) return ok({ results: fallbackResults(), usage })

      let toolInput: unknown
      try { toolInput = JSON.parse(jsonMatch[0]) } catch {
        return ok({ results: fallbackResults(), usage })
      }

      const toolParsed = ToolResultSchema.safeParse(toolInput)
      if (!toolParsed.success) return ok({ results: fallbackResults(), usage })

      const results: BatchMentionResult[] = toolParsed.data.results.map((r) => ({
        keywordId: r.prompt_id,
        promptText: promptMap.get(r.prompt_id) ?? '',
        mentioned: r.mentioned,
        rankPosition: r.rank_position ?? null,
        sentiment: r.sentiment ?? null,
        contextSnippet: r.context_snippet ?? null,
        competitorMentions: r.competitor_mentions ?? [],
        aiAnswer: r.ai_answer ?? null,
      }))

      const response: BatchResponse = { results, usage }
      return ok(response)
    },
  }
}
